import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { TranslateModule } from "@ngx-translate/core";
import { FormPrefixComponent } from "src/app/components/global/prefixes/containers/form-prefix/form-prefix.component";
import { PrefixEvent } from "src/app/components/global/prefixes/prefix.component";
import { LinkData } from "src/app/components/global/prefixes/templates/template-link/template-link.component";
import { DefaultComponent } from "src/app/default.component";
import { TooltipDirective } from "src/directives/tooltip.directive";
import { IconReplacePipe } from "src/pipes/icon-replace.pipe";
import { ApplicationService } from "src/services/application.service";
import { TableGroupAmountPipe } from "../../../../../../../pipes/table-group-amount.pipe";
import { TablePrefixComponent } from "../../../../../global/prefixes/containers/table-prefix/table-prefix.component";
import { TableField } from "../../../table-field";
import { TableGroup } from "../../../table-group";
import { TableHeader } from "../../../table-header";
import { TableRow } from "../../../table-row";
import { TableOptions } from "../../../table.interface";
import { EditEvent } from "../../table-view.component";

@Component({
  selector: "app-table-list-view-group",
  imports: [
    CommonModule,
    MatIconModule,
    MatCheckboxModule,
    TablePrefixComponent,
    TranslateModule,
    MatExpansionModule,
    IconReplacePipe,
    TableGroupAmountPipe,
    TooltipDirective,
    FormPrefixComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./table-list-view-group.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./table-list-view-group.component.less",
})
export class TableListViewGroupComponent extends DefaultComponent implements OnInit {
  @Input({ required: true })
  public headers: TableHeader[];
  @Input({ required: true })
  public group: TableGroup;
  @Input({ required: true })
  public options: TableOptions;

  @Output()
  public rowSelection: EventEmitter<void>;
  @Output()
  public prefixEvent: EventEmitter<[PrefixEvent, TableRow]>;
  @Output()
  public sort: EventEmitter<TableHeader>;

  @Output()
  public editEvent: EventEmitter<EditEvent>;

  public showOpenInNewTab: boolean;

  private application = inject(ApplicationService);

  public constructor() {
    super();
    this.headers = [];
    this.group = new TableGroup("");
    this.options = {
      head: true,
      foot: true,
      delete: false,
      archive: false,
      add: false,
      download: false,
      search: true,
      selectable: false,
      type: "DEFAULT",
      filter: false,
      map: false,
      tile: false,
      pageSize: 0,
      layout: null,
    };
    this.rowSelection = new EventEmitter();
    this.prefixEvent = new EventEmitter();
    this.editEvent = new EventEmitter();
    this.sort = new EventEmitter();
    this.showOpenInNewTab = false;
  }

  public ngOnInit(): void {
    this.showOpenInNewTab = this.group.rows.every((row) => {
      const contentId = this.getContentIdFromRow(row);
      return !!contentId;
    });
  }

  public onOpenLink(event: MouseEvent, row: TableRow): void {
    const contentId = this.getContentIdFromRow(row);

    if (contentId) this.application.splitscreen.next({ id: contentId, type: "reading-pane" });
  }

  public async onFieldClicked(row: TableRow, field: TableField): Promise<void> {
    const contentId = this.getContentIdFromRow(row);
    console.log(contentId, field.formfield_name);
    if (contentId && field.formfield_name) this.editEvent.emit({ contentId, row, field, type: "REQUEST" });
  }

  public async onFieldSubmitted(row: TableRow, field: TableField): Promise<void> {
    const contentId = this.getContentIdFromRow(row);
    console.log(contentId, field.formfield_name);
    if (contentId && field.formfield_name) this.editEvent.emit({ contentId, row, field, type: "SUBMIT" });
  }

  private getContentIdFromRow(row: TableRow): string | undefined {
    let contentId: string | undefined;

    const link = row.fields.find((x) => x.name === "idpID");
    if (link && link.extras && "links" in link.extras) {
      const { links } = <{ links: LinkData[] }>link.extras;
      contentId = links.at(0)?.link;
    }

    return contentId;
  }
}
